import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import ImageUploader from "components/common/ImageUploader";
import { Card, Form } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { EditarUsuarioDtoType, FormValues } from "../userTypes";
import { formatCPF, formatPhoneNumber } from "../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faKey } from "@fortawesome/free-solid-svg-icons";
import { Button, InputGroup } from "react-bootstrap";
import { useState } from "react";
import GrupoSelect from "../GrupoSelect";

// Fetch function outside of component
const fetchUsuario = async (id: string) => {
  const response = await axios.get(`/api/usuario/${id}`);

  // Extract the actual user data from the Result wrapper
  const userData = response.data.data;

  if (!userData) {
    throw new Error(response.data.message || "Erro ao carregar usuário");
  }

  return userData;
};

interface InformacoesUsuarioProps {
  isNewUser?: boolean; // Added to support new user creation
  imageEndpoint?: string; // Added to support custom image endpoint
}

const InformacoesUsuario = ({
  isNewUser = false,
  imageEndpoint,
}: InformacoesUsuarioProps) => {
  const { id } = useParams<{ id: string }>();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Specify the type of the form context to match FormValues from userTypes
  const {
    register,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext<FormValues>();

  // Only fetch user data if we're editing (not creating new)
  const { isLoading, isError, data } = useQuery<EditarUsuarioDtoType>({
    queryKey: ["usuario", id],
    queryFn: async () => {
      const userData = await fetchUsuario(id!);
      return userData;
    },
    enabled: !!id && !isNewUser, // Only run query if id exists and not creating a new user
  });

  const handleImageUploaded = (imageUrl: string | null) => {
    // Then, update the form field synchronously
    setValue("imageUrl", imageUrl || undefined, { shouldDirty: true });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const formattedPhone = formatPhoneNumber(value);
    setValue("telefone", formattedPhone);
  };

  // Função para obter a primeira letra do nome do usuário
  const getNameInitial = (): string => {
    const nome = watch("nome");
    if (nome && nome.length > 0) {
      return nome.charAt(0);
    }
    return "";
  };

  return (
    <Card className={`mb-4 `}>
      <div className={`${data?.isDeleted ? "position-relative" : ""}`}>
        {data?.isDeleted && (
          <div
            className="position-absolute top-0 start-0 h-100"
            style={{
              width: "4px",
              backgroundColor: "#dc3545",
              borderTopLeftRadius: "4px",
              borderBottomLeftRadius: "4px",
            }}
          ></div>
        )}
        <Card.Body>
          <h5 className="mb-3">Informações do Usuário</h5>

          <div className="row">
            <div className="col-md-3 mb-4 mb-md-0">
              <div className="d-flex flex-column align-items-center">
                <ImageUploader
                  endpoint={
                    imageEndpoint ||
                    (isNewUser
                      ? "/api/usuario/imagem"
                      : `/api/usuario/${id}/imagem`)
                  }
                  imageUrl={watch("imageUrl") || ""}
                  onImageUploaded={handleImageUploaded}
                  size="lg"
                  shape="circle"
                  className="mb-3"
                  nameInitial={getNameInitial()}
                  isNewUser={isNewUser}
                />
                <small className="text-muted mt-2">
                  Tamanho máximo: 700KB
                  <br />
                  Formatos: JPG, JPEG, PNG
                </small>
              </div>
            </div>

            <div className="col-md-9">
              <div className="row g-3">
                {!isNewUser && (
                  <div className="col-12 d-flex align-items-end">
                    <Form.Group
                      controlId="codigo"
                      className="me-3"
                      style={{ width: "110px" }}
                    >
                      <Form.Label>Código</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Auto-gerado"
                        {...register("codigo")}
                        disabled
                        readOnly
                      />
                    </Form.Group>

                    <Form.Group controlId="nome" className="me-3 flex-grow-1">
                      <Form.Label>
                        Usuário <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Nome de usuário"
                        {...register("nome", {
                          required: "Usuário é obrigatório",
                        })}
                        isInvalid={!!errors.nome}
                        disabled={data?.isDeleted}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.nome?.message}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group
                      controlId="telefone"
                      className="me-3"
                      style={{ width: "180px" }}
                    >
                      <Form.Label>Telefone</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="(XX) XXXXX-XXXX"
                        value={watch("telefone")}
                        onChange={handlePhoneChange}
                        maxLength={15}
                        disabled={data?.isDeleted}
                      />
                    </Form.Group>
                  </div>
                )}

                {/* For new user, show different layout */}
                {isNewUser && (
                  <div className="col-12">
                    <Form.Group controlId="nome" className="me-3">
                      <Form.Label>
                        Usuário <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Nome de usuário"
                        {...register("nome", {
                          required: "Usuário é obrigatório",
                        })}
                        isInvalid={!!errors.nome}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.nome?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                )}

                <div className="col-12 d-flex">
                  <Form.Group
                    controlId="nomeCompleto"
                    className="me-3 flex-grow-1"
                  >
                    <Form.Label>
                      Nome Completo <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Nome completo do usuário"
                      {...register("nomeCompleto", {
                        required: "Nome completo é obrigatório",
                      })}
                      isInvalid={!!errors.nomeCompleto}
                      disabled={data?.isDeleted}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.nomeCompleto?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group controlId="cpf" className="me-3" style={{ width: "180px" }}>
                    <Form.Label>
                      CPF
                      {watch("isSngpc") && (
                        <span className="text-danger">*</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="000.000.000-00"
                      value={watch("cpf")}
                      maxLength={14}
                      isInvalid={!!errors.cpf}
                      disabled={data?.isDeleted}
                      {...register("cpf", {
                        onChange: (e) => {
                          const { value } = e.target;
                          const formattedCPF = formatCPF(value);
                          setValue("cpf", formattedCPF);
                        },

                        validate: (value) => {
                          if (watch("isSngpc") && (!value || value.trim() === "")) {
                            return "CPF é obrigatório para Responsáveis Técnicos SNGPC";
                          }
                          return true;
                        }
                      })}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.cpf?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group controlId="crf" style={{ width: "140px" }}>
                    <Form.Label>
                      CRF
                      {watch("isSngpc") && (
                        <span className="text-danger">*</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="UF-00000"
                      {...register("crf", {
                        validate: (value) => {
                          if (watch("isSngpc") && (!value || value.trim() === "")) {
                            return "CRF é obrigatório para Responsáveis Técnicos SNGPC";
                          }
                          return true;
                        }
                      })}
                      isInvalid={!!errors.crf}
                      disabled={data?.isDeleted}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.crf?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>

                <div className="col-12">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Form.Group controlId="email">
                        <Form.Label>
                          Email <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="email@exemplo.com"
                          {...register("email", {
                            required: "Email é obrigatório",
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: "Email inválido",
                            },
                          })}
                          isInvalid={!!errors.email}
                          disabled={data?.isDeleted}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>

                    <div className="col-md-6">
                      <GrupoSelect
                        register={register}
                        errors={errors}
                        isDisabled={data?.isDeleted}
                        required={true}
                      />
                    </div>
                  </div>
                </div>

                {/* Display different password fields based on whether it's a new user or edit form */}
                {isNewUser ? (
                  <>
                    <div className="col-md-6">
                      <Form.Group controlId="senha">
                        <Form.Label>
                          Senha <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            placeholder="Digite a senha"
                            autoComplete="new-password"
                            {...register("senha", {
                              required: "Senha é obrigatória",
                              minLength: {
                                value: 6,
                                message:
                                  "Senha deve ter pelo menos 6 caracteres",
                              },
                            })}
                            isInvalid={!!errors.senha}
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={togglePasswordVisibility}
                            className="rounded-end"
                          >
                            <FontAwesomeIcon
                              icon={showPassword ? faEyeSlash : faEye}
                            />
                          </Button>
                        </InputGroup>
                        <Form.Control.Feedback type="invalid">
                          {errors.senha?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="confirmarSenha">
                        <Form.Label>
                          Confirmar Senha <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirme a senha"
                            autoComplete="new-password"
                            {...register("confirmarSenha", {
                              required: "Confirmação de senha é obrigatória",
                              validate: (value) =>
                                value === watch("senha") ||
                                "As senhas não correspondem",
                            })}
                            isInvalid={!!errors.confirmarSenha}
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={toggleConfirmPasswordVisibility}
                            className="rounded-end"
                          >
                            <FontAwesomeIcon
                              icon={showConfirmPassword ? faEyeSlash : faEye}
                            />
                          </Button>
                        </InputGroup>
                        <Form.Control.Feedback type="invalid">
                          {errors.confirmarSenha?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-md-6">
                      <Form.Group controlId="novaSenha">
                        <Form.Label>
                          <FontAwesomeIcon icon={faKey} className="me-1" />
                          Nova Senha
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            placeholder="Digite para alterar a senha"
                            {...register("novaSenha", {
                              minLength: {
                                value: 6,
                                message:
                                  "A senha deve ter pelo menos 6 caracteres",
                              },
                            })}
                            isInvalid={!!errors.novaSenha}
                            disabled={data?.isDeleted}
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={togglePasswordVisibility}
                            className="rounded-end"
                            disabled={data?.isDeleted}
                          >
                            <FontAwesomeIcon
                              icon={showPassword ? faEyeSlash : faEye}
                            />
                          </Button>
                          <Form.Control.Feedback type="invalid">
                            {errors.novaSenha?.message}
                          </Form.Control.Feedback>
                        </InputGroup>
                        <Form.Text className="text-muted">
                          Deixe em branco para manter a senha atual
                        </Form.Text>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="confirmarSenha">
                        <Form.Label>
                          <FontAwesomeIcon icon={faKey} className="me-1" />
                          Confirmar Senha
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirme a nova senha"
                            {...register("confirmarSenha", {
                              validate: {
                                matchesPassword: (value) => {
                                  const novaSenha = watch("novaSenha");
                                  // Only validate if nova senha has a value
                                  if (!novaSenha) return true;
                                  // Check that both values match exactly
                                  return (
                                    novaSenha === value ||
                                    "As senhas não correspondem"
                                  );
                                },
                              },
                            })}
                            isInvalid={!!errors.confirmarSenha}
                            disabled={data?.isDeleted}
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={toggleConfirmPasswordVisibility}
                            className="rounded-end"
                            disabled={data?.isDeleted}
                          >
                            <FontAwesomeIcon
                              icon={showConfirmPassword ? faEyeSlash : faEye}
                            />
                          </Button>
                          <Form.Control.Feedback type="invalid">
                            {errors.confirmarSenha?.message}
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Form.Group>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </Card.Body>
      </div>
    </Card>
  );
};

export default InformacoesUsuario;
